<template>
  <div>
    <form>
      <fieldset>
        <legend>{{ $t('receipt.actionForm') }}</legend>
        <div class="row">
          <div class="col-md-3">
            <label class="ts-mb-0 required">{{
              $t('receipt.paymentDate')
            }}</label>
            <a-date-picker
              v-model="model.payment_date"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 100%; height: 35px"
              class="mb-2"
            />
            <div class="validate" v-if="errors.has('payment_date')">
              {{ errors.first('payment_date') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class="ts-mb-0">{{ $t('receipt.exchangeRate') }}</label>
            <input
              type="text"
              class="form-control mb-2"
              readonly
              v-model.number="model.exchange_rate"
            />
          </div>
          <div class="col-md-3">
            <label class=" ts-mb-0 required">{{
              $t('receipt.invoiceNumber')
            }}</label>
            <input
              v-model="model.invoice_number"
              type="text"
              class="form-control mb-2"
              readonly
            />
            <div class="validate" v-if="errors.has('invoice_number')">
              {{ errors.first('invoice_number') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class="ts-mb-0">{{ $t('receipt.amount') }}</label>
            <input
              type="text"
              class="form-control mb-2"
              readonly
              v-model.number="model.inv_amount"
            />
            <div class="validate" v-if="errors.has('inv_amount')">
              {{ errors.first('inv_amount') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class=" ts-mb-0 required">{{
              $t('receipt.paidAmountUsd')
            }}</label>
            <input
              ref="amountpaidusd"
              type="text"
              class="form-control mb-2"
              :class="{ 'is-invalid': errors.has('amount_paid_usd') }"
              v-model.number="model.amount_paid_usd"
              @input="calculateAmountDue"
            />
            <div class="invalid-feedback" v-if="errors.has('amount_paid_usd')">
              {{ errors.first('amount_paid_usd') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class="ts-mb-0">{{ $t('receipt.paidAmountKhr') }}</label>
            <input
              ref="amount_paid_khr"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.has('amount_paid_khr') }"
              v-model.number="model.amount_paid_khr"
              @input="calculateAmountDue"
            />
            <div class="invalid-feedback" v-if="errors.has('amount_paid_khr')">
              {{ errors.first('amount_paid_khr') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class="ts-mb-0">{{ $t('receipt.amountDue') }}</label>
            <input
              type="text"
              class="form-control"
              v-model.number="model.amount_due"
              readonly
            />
            <div class="validate" v-if="errors.has('amount_due')">
              {{ errors.first('amount_due') }}
            </div>
          </div>
          <div class="col-md-3">
            <label
              class="ts-mb-0"
              :class="{ required: model.amount_due > 0 }"
              >{{ $t('receipt.nextPaymentDate') }}</label
            >
            <a-date-picker
              :disabled="disableNextPayment"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 100%; height: 35px"
              v-model="model.next_payment_date"
            />
            <div class="validate" v-if="errors.has('next_payment_date')">
              {{ errors.first('next_payment_date') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class="ts-mb-0 required">{{
              $t('receipt.paymentMethod')
            }}</label>
            <select
              class="form-control"
              v-model="model.payment_method_id"
              @change="onPaymentMethodChange"
            >
              <option
                v-for="(p, index) in paymentMethods"
                :key="index"
                :value="p.id"
                >{{ p.label }}</option
              >
            </select>
            <div class="ts-text-red-600" v-if="errors.has('payment_method_id')">
              {{ errors.first('payment_method_id') }}
            </div>
          </div>
          <div class="col-md-3">
            <label class="ts-mb-0" :class="{ required: isRequired }">{{
              $t('receipt.bank')
            }}</label>
            <a-select
              v-model.number="model.bank_id"
              show-search
              optionFilterProp="children"
              :default-active-first-option="false"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              :options="branchList"
              :disabled="!isRequired"
            ></a-select>
            <div class="ts-text-red-600" v-if="errors.has('bank_id')">
              {{ errors.first('bank_id') }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="ts-mb-0">{{ $t('receipt.comment') }}</label>
            <textarea
              v-model="model.comment"
              rows="1"
              class="form-control mb-2"
            ></textarea>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 text-right">
            <slot name="button-action"></slot>
          </div>
        </div>
      </fieldset>
    </form>
  </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'
import moment from 'moment'
import { round } from 'lodash'

export default {
  name: 'receiptFormComponent',
  props: ['value', 'validate', 'banks'],
  data() {
    return {
      model: {},
      errors: new Errors()
    }
  },
  created() {
    this.model = this.value
    this.$nextTick(() => {
      this.$refs.amountpaidusd.focus()
    })
  },
  computed: {
    ...mapState('receipt', ['defaultCreditDay']),
    disableNextPayment() {
      return !this.model.amount_due > 0
    },
    isRequired() {
      return this.model.payment_method_id !== 1
    },
    branchList() {
      return this.banks.map(el => ({
        value: el.bank_id,
        label: el.bank_name
      }))
    },
    paymentMethods() {
      return [
        { id: 1, label: 'Cash' },
        { id: 2, label: 'Cheque' },
        { id: 3, label: 'Bank Transfer' },
        { id: 4, label: 'Credit Card' }
      ]
    }
  },
  methods: {
    onPaymentMethodChange() {
      this.model.bank_id = null
    },
    calculateAmountDue() {
      this.model.amount_paid = this.model.amount_paid_usd + (this.model.amount_paid_khr > 0 ? this.model.amount_paid_khr / this.model.exchange_rate : 0)
      this.model.amount_due = round(this.model.inv_amount - this.model.amount_paid, 2)
      if (this.model.amount_due > 0) {
        this.model.next_payment_date = moment()
          .add(this.defaultCreditDay, 'Day')
          .format('DD-MM-YYYY')
      } else {
        this.model.next_payment_date = null
        this.model.amount_due = 0
      }
    }
  },
  watch: {
    validate: function(error) {
      this.errors = new Errors(error.errors)
    },
    'model.amount_due': function(value) {
      if (value == 0) {
        this.model.next_payment_date = null
      }
    }
  }
}
</script>

<style lang="css" scoped>
label {
  font-size: 12px !important;
}
</style>
